/**
 * The cheques a total is made of, each linked to the document it came off.
 *
 * Every money figure on this site is a sum of rows like these, and until now
 * none of them could be opened. That is not a missing convenience: it is how
 * a wrong figure survived. A chart edge read "Apple Inc. → Josh Riley,
 * $26,100, 9 cheques", and two of the nine were one semi-annual report
 * counted twice — which nobody could see from the site, because the site
 * offered no route from the total to the nine.
 *
 * Three things each row has to carry, and they are the three things a
 * reader gets wrong without them.
 *
 * **Where it came from.** `source` is the register's own print view of the
 * LD-203, so the sentence "this is what the filing says" can be checked
 * against the filing in one click.
 *
 * **Who wrote it.** A contribution filed by an individual lobbyist is that
 * person's own money, with the organisation on the form only as their
 * employer. A corporation cannot lawfully give to a federal candidate at
 * all, so a list that renders both the same way tells the reader a crime
 * took place.
 *
 * **When, approximately.** The date is as filed, and filers routinely enter
 * the last day of the reporting period rather than the day of the cheque.
 * Four of Apple's nine were dated 31 March, 30 June or 31 December, and the
 * FEC has 1 November where this record says the 11th.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { type Cheque, type Cheques as ChequePage, api } from "../api";
import { useAsync, useDebounced } from "../useAsync";
import { Problem, Spinner } from "./bits";

/** How many rows the section shows before it says how many more there are. */
const PAGE = 50;

/** What the register calls each kind of money, short enough for a row. */
const KIND: Record<string, string> = {
  feca: "to a campaign",
  he: "event in their honour",
  ple: "presidential library",
  pic: "inaugural committee",
  me: "meeting or retreat",
};

/**
 * Who filed the row, in the fewest words that cannot be misread.
 *
 * "Personal" rather than "lobbyist" because the reader is looking at an
 * organisation's page: the useful distinction is whose money it was, not
 * which box on the form was ticked.
 */
function filedBy(cheque: Cheque): string {
  if (cheque.filer_type === "lobbyist") {
    return cheque.lobbyist ? `${cheque.lobbyist}, personally` : "a lobbyist, personally";
  }
  return "the organisation";
}

export function FirmCheques({ registrantId, name }: { registrantId: number; name: string }) {
  const [query, setQuery] = useState("");
  /** Searched in SQL rather than over the rows already fetched. This section
   *  shows 50 of a total that runs to thousands, so filtering what arrived
   *  would answer "nothing filed" about a recipient with a dozen cheques in
   *  the record — the one wrong answer this page must not give. */
  const settled = useDebounced(query);
  const found = useAsync(
    () => api.firmGifts(registrantId, { q: settled || undefined, limit: PAGE }),
    [registrantId, settled],
  );

  if (found.error) return <Problem error={found.error} retry={found.reload} />;
  if (!found.data) return <Spinner label="Reading the filings…" />;

  return <ChequeList page={found.data} name={name} query={query} onQuery={setQuery} />;
}

/**
 * The rows, as a pure render.
 *
 * Split from the fetch so the wording and the links can be tested without a
 * network: the point of this section is what it says, and a component that
 * can only be checked by running the site is one whose sentences drift.
 */
export function ChequeList({
  page,
  name,
  query = "",
  onQuery,
}: {
  page: ChequePage;
  name: string;
  query?: string;
  onQuery?: (query: string) => void;
}) {
  const shown = page.gifts.length;
  return (
    <div>
      {onQuery && (
        <div className="mt-4">
          <label htmlFor="cheque-search" className="text-sm text-muted">
            Show only what went to
          </label>
          <input
            id="cheque-search"
            type="search"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder="a member, a committee, a library…"
            className="mt-1.5 block w-full max-w-md rounded-xl border border-line bg-card px-4 py-2.5 text-sm shadow-sm placeholder:text-faint"
          />
          {/* Says which rows it searched, because the list below is a page
              of a much longer record and the reader cannot see that. */}
          <p className="mt-1.5 text-xs text-faint">
            Searches all cheques on file, by member, by the name the filer typed, or by the
            committee the cheque was made out to.
          </p>
        </div>
      )}

      {shown === 0 ? (
        <p className="mt-4 text-muted">
          {query ? `Nothing filed against “${query}”.` : "Nothing filed."}
        </p>
      ) : (
        <ol className="mt-4 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
          {page.gifts.map((cheque) => (
            <li
              key={cheque.contribution_id}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3"
            >
              <span className="w-20 font-medium tabular-nums">
                ${Math.round(cheque.amount).toLocaleString()}
              </span>
              <span className="min-w-48 flex-1">
                {cheque.bioguide ? (
                  <Link to={`/member/${cheque.bioguide}`} className="font-medium hover:text-brand">
                    {cheque.full_name ?? cheque.honoree}
                  </Link>
                ) : (
                  <span className="font-medium">{cheque.honoree || cheque.payee}</span>
                )}
                <span className="text-sm text-muted"> · filed by {filedBy(cheque)}</span>
              </span>
              <span className="text-sm text-faint">
                {KIND[cheque.kind] ?? cheque.kind} · {cheque.date ?? cheque.filing_year}
              </span>
              {cheque.source ? (
                <a
                  href={cheque.source}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-brand hover:underline"
                >
                  The filing
                </a>
              ) : (
                <span className="text-sm text-faint">no filing id</span>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="mt-3 max-w-3xl space-y-2 text-sm text-faint">
        {page.total > shown && (
          <p>
            The {shown} most recent of {page.total.toLocaleString()}.
          </p>
        )}
        {/* The sentence the whole section exists for. Without it a reader
            reasonably concludes the company wrote the cheque. */}
        <p>
          “Personally” means the named lobbyist filed it on their own report as their own money,
          with {name} on the form as their employer. {name} did not give it, and a corporation may
          not contribute to a federal candidate at all.
        </p>
        {/* Four of the nine cheques that started this were dated to the end
            of a reporting period rather than to the day they were written. */}
        <p>
          Dates are as filed and are approximate. Filers regularly enter the last day of the
          reporting period — 31 March, 30 June, 31 December — rather than the day of the cheque, so
          the FEC's own record of the same contribution can differ by days.
        </p>
      </div>
    </div>
  );
}
